/**
 * Small helper for "destructive action with 5-second undo".
 *
 * Usage:
 *   await runWithUndo({
 *     applyOptimistic: () => setSessions(cur => cur.filter(s => s.id !== id)),
 *     restore: () => setSessions(prev),
 *     commit: () => api.delete(`/sessions/${id}`),
 *     toastMsg: 'Session deleted',
 *     undoMsg: 'Restored',
 *     onError: (err) => toast.error(err.response?.data?.detail || 'Failed'),
 *   });
 *
 * Behaviour:
 *  - applyOptimistic runs immediately (UI flips)
 *  - toast appears with "Undo" action for 5 seconds
 *  - if undo clicked → restore() runs, commit is never called
 *  - if not clicked → commit() runs after 5s; on error → restore() + onError()
 */
import { toast } from 'sonner';

const UNDO_WINDOW_MS = 5000;

export async function runWithUndo({
  applyOptimistic,
  restore,
  commit,
  toastMsg,
  undoMsg = 'Restored',
  onError,
}) {
  applyOptimistic();

  return new Promise((resolve) => {
    let undone = false;
    const toastId = toast(toastMsg, {
      duration: UNDO_WINDOW_MS,
      action: {
        label: 'Undo',
        onClick: () => {
          undone = true;
          try { restore(); } catch (_) { /* noop */ }
          toast.dismiss(toastId);
          toast.success(undoMsg);
          resolve({ undone: true });
        },
      },
    });

    setTimeout(async () => {
      if (undone) return;
      try {
        await commit();
        resolve({ undone: false });
      } catch (err) {
        try { restore(); } catch (_) { /* noop */ }
        if (onError) onError(err);
        else toast.error(err?.response?.data?.detail || 'Action failed');
        resolve({ undone: false, error: err });
      }
    }, UNDO_WINDOW_MS);
  });
}
